"use client";

import { QueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";

import { isLocalNoteId } from "@/hooks/use-selected-note";

import { validateAudioBlob } from "@/lib/audio";
import { parseError } from "@/lib/parse-error";
import { logTimestamp } from "@/lib/transcription-queue";
import type {
  TranscriptionOutcome,
  TranscriptionQueueItem,
} from "@/lib/transcription-queue/types";
import { toast } from "@/lib/use-toast";

import { NoteDetail } from "@/types/notes";

import { startRecordingSessionAction } from "@/api/provider/scribe/note-mutation";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";
import { transcribeNote } from "@/api/provider/scribe/notes";
import { useScribeStore } from "@/stores/scribe-store";

type StartOptions = {
  deviceId?: string;
  mimeType?: string;
  chunkMs?: number;
  resetTimer?: boolean;
};

export type ScribeTranscriptionDeps = {
  queryClient: QueryClient;
  currentUserId?: string;
  currentLocationId?: string;
  selectedNoteId?: string;

  startAudio: (opts: {
    deviceId?: string;
    mimeType?: string;
    chunkMs?: number;
    resetTimer?: boolean;
    providerId?: string;
    locationId?: string;
    patientId?: string;
    stream?: MediaStream;
  }) => Promise<void>;
  /** Opens the mic recording will use; rejects if the browser refuses. */
  acquireMic: () => Promise<MediaStream>;

  patientId?: string;
  /**
   * Patient AI scribe consent (null = not asked, true = opted in,
   * false = opted out via intake form). When false the backend disables
   * AI note generation; callers may pass `overrideOptOut` after confirming
   * in-office that the patient has changed their mind.
   */
  patientAiScribeConsent?: boolean | null;
  /** Re-enable AI generation for this session even if the patient opted out. */
  overrideOptOut?: boolean;
  currentRecordingId?: string;
  setRecordingId: (id: string) => void;
  /** Swaps the local draft for its new server note, keeping what the draft carried. */
  promoteDraftRecording: (id: string) => void;
  onScribePage: boolean;
  subDomain: string;
  navigateWithProgress: (href: string) => void;
  linkedEventId?: string | null;
  recordingNoteTemplateId?: string | null;
};

/**
 * Starts a recording session with transcription.
 *
 * This function:
 * 1. Opens the microphone, if a note is about to be created
 * 2. Ensures a server-side note exists (creates one if needed)
 * 3. Starts the audio recorder with the queue-based transcription system
 *
 * Audio chunks are automatically enqueued by the scribe store's onstop handler.
 * The transcription queue handles retries, persistence, and error isolation.
 */
export async function startRecordingWithTranscription(
  options: StartOptions = {},
  deps: ScribeTranscriptionDeps,
): Promise<string> {
  const {
    patientId,
    patientAiScribeConsent,
    overrideOptOut,
    currentUserId,
    currentLocationId,
    queryClient,
    setRecordingId,
    startAudio,
    acquireMic,
    currentRecordingId,
    onScribePage,
    selectedNoteId,
    subDomain,
    navigateWithProgress,
    linkedEventId,
    recordingNoteTemplateId,
    promoteDraftRecording,
  } = deps;

  const needsNewNote = !currentRecordingId || isLocalNoteId(currentRecordingId);

  const ensureServerNote = async (): Promise<{
    noteId: string;
    createdDate?: string;
  }> => {
    if (currentRecordingId && !needsNewNote) {
      return { noteId: currentRecordingId };
    }

    if (!patientId || !currentUserId) {
      throw new Error("Patient or user information is missing");
    }

    const newNote = await startRecordingSessionAction(
      patientId,
      currentLocationId ?? undefined,
      overrideOptOut,
      linkedEventId,
      recordingNoteTemplateId,
    );
    if (!newNote) throw new Error("Failed to create note session");
    // Patient has opted out of AI scribe via intake form and the provider
    // hasn't explicitly overridden — surface a warning. Manual dictation
    // still works; AI generation is skipped for this note.
    if (patientAiScribeConsent === false && !overrideOptOut) {
      toast({
        title: "AI scribe disabled for this patient",
        description:
          "This patient opted out of AI scribe on their intake form. Recording continues for manual dictation, but no AI note will be generated.",
      });
    }
    queryClient.invalidateQueries({
      queryKey: noteQueryKeys.list({}),
    });
    promoteDraftRecording(newNote.id);
    if (onScribePage && isLocalNoteId(selectedNoteId) && subDomain) {
      navigateWithProgress(`/scribe/playground/${newNote.id}`);
    }
    return {
      noteId: newNote.id,
      createdDate: newNote.createdDate,
    };
  };

  // Hold the mic across note creation and record from that same stream: a
  // mic that is refused, or that vanishes while the note is being created,
  // would otherwise leave an empty note on the chart.
  const micStream = needsNewNote ? await acquireMic() : undefined;
  let noteId: string;
  try {
    ({ noteId } = await ensureServerNote());
  } catch (e) {
    micStream?.getTracks().forEach((t) => t.stop());
    throw e;
  }
  posthog.startSessionRecording({ sampling: true });

  // Start audio recording - chunks are enqueued by the onstop handler in scribe-store
  // The queue handles transcription, retries, and persistence automatically
  await startAudio({
    deviceId: options.deviceId,
    mimeType: options.mimeType,
    chunkMs: options.chunkMs ?? 30000,
    resetTimer: options.resetTimer,
    providerId: currentUserId,
    locationId: currentLocationId,
    patientId,
    stream: micStream,
  });
  return noteId;
}

/**
 * Dependencies needed to create a chunk processor for the queue.
 */
export interface ChunkProcessorDeps {
  queryClient: QueryClient;
  setTranscriptionErrorNoteId: (noteId: string | null) => void;
  setRecordingId: (id: string) => void;
}

const CHUNK_LOG_PREFIX = () =>
  `[${logTimestamp()}][TranscriptionQueue:ChunkProcessor]`;

/**
 * Creates a chunk processor function for the transcription queue.
 *
 * This function is called by the queue for each chunk that needs to be
 * transcribed. It handles validation, API calls, and error classification.
 *
 * @param deps - Dependencies for the processor
 * @returns A function that processes a single chunk
 */
export function createChunkProcessor(
  deps: ChunkProcessorDeps,
): (item: TranscriptionQueueItem, blob: Blob) => Promise<TranscriptionOutcome> {
  const { queryClient, setTranscriptionErrorNoteId, setRecordingId } = deps;

  return async (
    item: TranscriptionQueueItem,
    blob: Blob,
  ): Promise<TranscriptionOutcome> => {
    console.log(
      `${CHUNK_LOG_PREFIX()} Processing chunk: id=${item.id}, noteId=${item.noteId}, size=${blob.size}`,
    );

    const type = blob.type || item.mimeType || "audio/webm";
    const file = new File([blob], "recording.webm", { type });

    // Validate audio blob before sending to backend
    const validation = await validateAudioBlob(blob, item.mimeType);
    if (!validation.isValid) {
      const errorMessage =
        validation.error || "Audio file is invalid or corrupted";
      console.warn(`${CHUNK_LOG_PREFIX()} Validation failed: ${errorMessage}`);
      // Validation failures are permanent - don't retry
      return {
        success: false,
        permanent: true,
        error: errorMessage,
      };
    }

    // Check required fields
    if (!item.patientId) {
      console.error(`${CHUNK_LOG_PREFIX()} Missing patientId`);
      return {
        success: false,
        permanent: true,
        error: "Patient information is missing for the recording session",
      };
    }
    if (!item.providerId) {
      console.error(`${CHUNK_LOG_PREFIX()} Missing providerId`);
      return {
        success: false,
        permanent: true,
        error: "Unable to authenticate user. Please try again later.",
      };
    }
    if (!item.noteId) {
      console.error(`${CHUNK_LOG_PREFIX()} Missing noteId`);
      return {
        success: false,
        permanent: true,
        error: "Note ID is missing. Please start recording first.",
      };
    }

    try {
      console.log(
        `${CHUNK_LOG_PREFIX()} Calling transcribeNote API: noteId=${item.noteId}`,
      );

      // Backend looks up transcript by note_id - no need to pass transcript ID
      const response = await transcribeNote(
        file,
        item.patientId,
        item.providerId,
        item.noteId,
        item.locationId,
      );

      if (!response) {
        console.warn(`${CHUNK_LOG_PREFIX()} Empty response from server`);
        // Null response is unexpected but not necessarily permanent
        return {
          success: false,
          permanent: false,
          error: "Empty response from server",
        };
      }

      console.log(
        `${CHUNK_LOG_PREFIX()} API success: transcriptId=${response.id}, noteId=${response.noteId}`,
      );

      // A chunk landed — clear any prior "error saving audio" state. The
      // queue retries failed chunks, so a transient failure must not leave
      // the badge stuck once a later chunk (or the retry) succeeds.
      setTranscriptionErrorNoteId(null);

      // Update React Query cache
      if (response.noteId) {
        queryClient.setQueryData<NoteDetail>(
          noteQueryKeys.detail(response.noteId),
          (prev) =>
            prev
              ? {
                  ...prev,
                  transcript: {
                    ...response,
                    transcript: response.transcript || "",
                  },
                }
              : prev,
        );
      }

      // Update store state
      setRecordingId(response.noteId);

      // Track transcript growth for silence auto-pause
      const newLength = (response.transcript ?? "").trim().length;
      const scribeState = useScribeStore.getState();
      // Only track the high-water mark for the currently active recording
      if (
        response.noteId === scribeState.currentRecordingId &&
        newLength > scribeState.prevTranscriptLength
      ) {
        useScribeStore.setState({
          lastTranscriptGrowthAt: Date.now(),
          prevTranscriptLength: newLength,
          noTranscriptWarningShown: false,
        });
      }

      return {
        success: true,
        transcriptId: response.id,
        noteId: response.noteId,
      };
    } catch (error) {
      // Let the queue processor classify the error for retry logic
      // We re-throw so the processor can inspect the HTTP status
      const errorMessage = await parseError(error);
      console.error(`${CHUNK_LOG_PREFIX()} API error: ${errorMessage}`, error);
      setTranscriptionErrorNoteId(item.noteId);

      // Show toast for user feedback
      toast({
        title: "Transcription Error",
        description: `${errorMessage}. Will retry automatically.`,
        variant: "error",
      });

      // Re-throw with original error for proper classification
      throw error;
    }
  };
}
